<template>
  <div class="vmg-public">
    <header class="vmg-header">
      <div class="vmg-container vmg-nav">
        <div class="vmg-brand">
          <span class="vmg-brand-mark">V</span>
          <div class="vmg-brand-text-block">
            <span class="vmg-brand-text">Veterinary Media.ai</span>
            <span class="vmg-brand-tagline">A Colibri Media Company</span>
          </div>
        </div>
        <nav class="vmg-nav-links">
          <a href="#try" class="vmg-link">Try it</a>
          <a href="#associations" class="vmg-link">Association Members</a>
          <a href="#about" class="vmg-link">Why Veterinary Media</a>
          <router-link to="/login" class="vmg-link vmg-link-cta">Sign In</router-link>
        </nav>
      </div>
    </header>

    <section id="about" class="vmg-hero">
      <div class="vmg-container vmg-hero-grid">
        <div>
          <p class="vmg-eyebrow vmg-members-badge">Registered MEMBERS only</p>
          <h1 class="vmg-h1">
            America&rsquo;s Official Veterinary Medical Knowledge Platform
          </h1>
          <p class="vmg-hero-sub">Get evidence-based answers with:</p>
          <p class="vmg-lede">
            Answers grounded in vetted veterinary evidence — not human medicine,
            not forum anecdotes. Free for verified veterinary professionals.
          </p>
          <ul class="vmg-bullets">
            <li>Differential diagnosis support</li>
            <li>Evidence-based treatment recommendations</li>
            <li>Procedural &amp; clinical technique references</li>
            <li>Visual learning — videos (lectures, clinical practices, surgeries)</li>
            <li>Curated CE library with RACE-approved courses</li>
          </ul>
          <div class="vmg-cta-row">
            <router-link to="/register" class="vmg-btn vmg-btn-primary">
              Sign Up / Join
            </router-link>
            <router-link to="/register-scav" class="vmg-btn vmg-btn-ghost">
              Verify Membership
            </router-link>
          </div>
        </div>
        <aside class="vmg-hero-card">
          <p class="vmg-card-eyebrow">Built for clinicians</p>
          <div class="vmg-card-stack">
            <div class="vmg-stack-row"><span>Evidence-based guidance</span></div>
            <div class="vmg-stack-row"><span>Cited answers you can trust</span></div>
            <div class="vmg-stack-row"><span>Text &amp; video responses</span></div>
            <div class="vmg-stack-row"><span>Integrated, on-demand CE library</span></div>
            <div class="vmg-stack-row"><span>Veterinary-only scope</span></div>
          </div>
          <p class="vmg-card-foot">
            No human-medicine results, no anecdotal forum posts —
            clinical answers designed for veterinary practice.
          </p>
        </aside>
      </div>
    </section>

    <section id="try" class="vmg-try">
      <div class="vmg-container">
        <h2 class="vmg-h2">Try a sample question</h2>
        <p class="vmg-sub">
          A few free questions per day — no signup required. Sign in for the full Vet Consult
          in Learning Hub: saved consults, CE courses, video clips, and Deep research mode.
        </p>

        <form class="vmg-try-form" @submit.prevent="askDemo">
          <textarea
            v-model="query"
            class="vmg-textarea"
            rows="3"
            maxlength="500"
            placeholder="e.g. Meloxicam dose for a 10kg dog with osteoarthritis"
            :disabled="loading"
          />
          <div class="vmg-form-actions">
            <span class="vmg-counter">{{ query.length }} / 500</span>
            <button
              type="submit"
              class="vmg-btn vmg-btn-primary"
              :disabled="!canSubmit"
            >
              {{ loading ? (progressButtonLabel || 'Working…') : 'Ask the agent' }}
            </button>
          </div>
          <!-- Cloudflare Turnstile widget renders here when site key is set -->
          <div
            v-if="turnstileSiteKey"
            ref="turnstileContainer"
            class="vmg-turnstile"
            data-callback="onTurnstileSuccess"
          />
        </form>

        <!-- Live progress while the (non-streaming) demo request runs.
             Mirrors the logged-in agent phases so visitors see work happening
             instead of a frozen "Searching evidence…" button. -->
        <div v-if="loading && progressSteps.length" class="vmg-progress" aria-live="polite">
          <div class="vmg-progress-header">
            <i class="fas fa-sitemap"></i>
            Deploying agents
          </div>
          <div
            v-for="(step, idx) in progressSteps"
            :key="step.key"
            class="vmg-progress-step"
            :class="{
              'is-done': step.done,
              'is-active': !step.done && idx === activeProgressIndex,
            }"
          >
            <span class="vmg-progress-icon">
              <i v-if="step.done" class="fas fa-check-circle"></i>
              <i v-else-if="idx === activeProgressIndex" class="fas fa-circle-notch fa-spin"></i>
              <i v-else class="far fa-circle"></i>
            </span>
            <span class="vmg-progress-text">{{ step.message }}</span>
          </div>
        </div>

        <div v-if="error" class="vmg-error">
          {{ error }}
        </div>

        <!-- Rate-limit wall: convert with one-click signup, not a form. -->
        <div v-if="errorIsLimit" class="vmg-signup-card">
          <h3 class="vmg-signup-title">Create a free account to keep going</h3>
          <p class="vmg-signup-sub">
            One click — no password to remember. You'll get unlimited questions plus the full CE video library.
          </p>
          <OAuthButtons
            :organization-slug="orgSlug"
            @success="onSignupSuccess"
          />
          <p class="vmg-signup-foot">
            Already a member?
            <router-link to="/login" class="vmg-inline-link">Sign in</router-link>
            · Veterinary license verification is optional and can be done later.
          </p>
        </div>

        <div v-if="answer" class="vmg-answer">
          <h3 class="vmg-answer-title">Sample Answer</h3>
          <div class="vmg-answer-body" v-html="formattedAnswer"></div>

          <!-- Research sources — same card layout as logged-in consult -->
          <div v-if="sources.length" class="vmg-sources">
            <h4 class="vmg-sources-title">
              <i class="fas fa-flask"></i>
              {{ sources.length }} research source{{ sources.length !== 1 ? 's' : '' }}
            </h4>
            <div class="vmg-sources-list">
              <div
                v-for="(src, i) in (showAllSources ? sources : sources.slice(0, 3))"
                :key="'src-' + (src.pmid || i)"
                class="vmg-source-item"
              >
                <div class="vmg-source-meta-row">
                  <span
                    class="vmg-source-db-badge"
                    :class="sourceDbBadgeClass(src.source_db)"
                  >{{ src.source_db || 'PubMed' }}</span>
                  <span v-if="src.journal" class="vmg-source-journal">{{ src.journal }}</span>
                  <span v-if="src.year" class="vmg-source-year">{{ src.year }}</span>
                </div>
                <a
                  :href="sourceHref(src)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="vmg-source-title-link"
                >
                  <span class="vmg-source-num">[{{ i + 1 }}]</span>
                  <span class="vmg-source-name">{{ src.title }}</span>
                </a>
                <p v-if="src.rationale" class="vmg-source-rationale">
                  <i class="fas fa-lightbulb"></i> {{ src.rationale }}
                </p>
                <div class="vmg-source-footer">
                  <span class="vmg-source-id">{{ sourceIdLabel(src) }}</span>
                  <a
                    v-if="src.open_access_pdf"
                    :href="src.open_access_pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="vmg-source-pdf"
                  >
                    <i class="fas fa-file-pdf"></i> PDF
                  </a>
                </div>
              </div>
              <button
                v-if="sources.length > 3"
                type="button"
                class="vmg-sources-more"
                @click="showAllSources = !showAllSources"
              >
                {{ showAllSources ? 'Show less' : `See ${sources.length - 3} more sources` }}
              </button>
            </div>
          </div>
          <!-- Locked CE / video teasers — visible but gated behind a free account -->
          <div v-if="hasTeasers" class="vmg-teasers">
            <h4 class="vmg-teasers-title">
              <i class="fas fa-lock"></i>
              Related learning — unlock with a free account
            </h4>
            <div class="vmg-teaser-grid">
              <div
                v-for="(c, i) in teasers.courses"
                :key="'course-' + i"
                class="vmg-teaser-card"
              >
                <div
                  class="vmg-teaser-thumb"
                  :style="c.thumbnail_url ? { backgroundImage: `url(${c.thumbnail_url})` } : {}"
                >
                  <span class="vmg-teaser-lock"><i class="fas fa-lock"></i></span>
                  <span v-if="c.credit_hours" class="vmg-teaser-badge">{{ c.credit_hours }} CE hrs</span>
                </div>
                <div class="vmg-teaser-body">
                  <span class="vmg-teaser-kind">Course</span>
                  <p class="vmg-teaser-name">{{ c.title }}</p>
                  <p v-if="c.instructor_name" class="vmg-teaser-meta">{{ c.instructor_name }}</p>
                </div>
              </div>
              <div
                v-for="(v, i) in teasers.videos"
                :key="'video-' + i"
                class="vmg-teaser-card"
              >
                <div class="vmg-teaser-thumb vmg-teaser-thumb-video">
                  <span class="vmg-teaser-lock"><i class="fas fa-lock"></i></span>
                  <span v-if="v.formatted_start" class="vmg-teaser-badge">{{ v.formatted_start }}</span>
                </div>
                <div class="vmg-teaser-body">
                  <span class="vmg-teaser-kind">Video clip</span>
                  <p class="vmg-teaser-name">{{ v.title }}</p>
                  <p v-if="v.course_title" class="vmg-teaser-meta">{{ v.course_title }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- One-click signup is the primary conversion path -->
          <div class="vmg-cta-card">
            <h4>{{ (cta && cta.headline) || 'Create a free account for unlimited access' }}</h4>
            <p>Sign up in one click to ask unlimited questions and watch the full CE video library.</p>
            <OAuthButtons
              :organization-slug="orgSlug"
              @success="onSignupSuccess"
            />
            <p class="vmg-cta-foot">
              Prefer email?
              <router-link to="/register" class="vmg-cta-link">Create account with email</router-link>
              · SCAV members
              <router-link to="/register-scav" class="vmg-cta-link">verify membership</router-link>
            </p>
          </div>
        </div>
      </div>
    </section>

    <section id="associations" class="vmg-scav">
      <div class="vmg-container vmg-scav-grid">
        <div>
          <p class="vmg-eyebrow">Association Members</p>
          <h2 class="vmg-h2">Partnering Veterinary Association / Society</h2>
          <p class="vmg-lede">
            Veterinary Association&rsquo;s members receive premium benefits
            including free CE, discounted courses, and association-branded
            certificates accepted by your state board &amp; RACE.
          </p>
          <div class="vmg-cta-row">
            <router-link to="/register" class="vmg-btn vmg-btn-primary">
              Sign Up / Join
            </router-link>
            <router-link to="/register-scav" class="vmg-btn vmg-btn-ghost">
              Verify Membership
            </router-link>
          </div>
        </div>
        <ul class="vmg-benefits">
          <li>Premium clinical consult features</li>
          <li>Cited text response</li>
          <li>Visual-learning library</li>
          <li>On-demand CE library</li>
          <li>Free CE</li>
          <li>40% off paid CE</li>
          <li>Association-branded CE certificates accepted by your state board &amp; RACE</li>
        </ul>
      </div>
    </section>

    <footer class="vmg-footer">
      <div class="vmg-container">
        <p>&copy; {{ year }} Veterinary Media.ai · A Colibri Media Company. Powered by Praetorian AI.</p>
        <p class="vmg-disclaimer">
          For licensed veterinary professionals. Not a substitute for clinical judgment.
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import { renderConsultMarkdown } from '@/utils/consultMarkdown'
import { learningHomeLocation } from '@/utils/associationPortal'
import OAuthButtons from '@/components/OAuthButtons.vue'

// Staged copy mirrors the authenticated consult_stream status events so the
// open-site demo feels like the same multi-agent pipeline — even though the
// public endpoint is a single POST (no SSE). Timings are paced for reassurance
// on a typical 8–20s synthesis; fast cache hits complete all steps at once.
const DEMO_PROGRESS_STEPS = [
  { key: 'deploying', message: 'Deploying agents…', button: 'Deploying agents…' },
  { key: 'scope', message: 'Verifying veterinary scope…', button: 'Checking scope…' },
  { key: 'searching', message: 'Searching PubMed, FDA labels & veterinary literature…', button: 'Searching evidence…' },
  { key: 'filtering', message: 'Filtering for veterinary relevance…', button: 'Filtering sources…' },
  { key: 'scoring', message: 'Scoring evidence quality…', button: 'Scoring evidence…' },
  { key: 'synthesizing', message: 'Synthesizing cited clinical answer…', button: 'Writing answer…' },
  { key: 'citing', message: 'Attaching research sources…', button: 'Citing sources…' },
]

const PROGRESS_STEP_MS = 2200

export default {
  name: 'VetMediaPublic',
  components: { OAuthButtons },
  data() {
    return {
      query: '',
      loading: false,
      answer: '',
      sources: [],
      teasers: { courses: [], videos: [] },
      cta: null,
      error: '',
      errorIsLimit: false,
      turnstileToken: '',
      turnstileWidgetId: null,
      progressSteps: [],
      activeProgressIndex: 0,
      progressTimer: null,
      showAllSources: false,
    }
  },
  computed: {
    orgSlug() {
      // New accounts created from the public demo belong to the Vet Media /
      // SCAV tenant, matching PUBLIC_DEMO_ORG_SLUG on the backend.
      return 'colibri-media-group'
    },
    hasTeasers() {
      return (this.teasers.courses && this.teasers.courses.length > 0)
        || (this.teasers.videos && this.teasers.videos.length > 0)
    },
    turnstileSiteKey() {
      return process.env.VUE_APP_TURNSTILE_SITE_KEY || ''
    },
    canSubmit() {
      if (this.loading) return false
      if (this.query.trim().length < 5) return false
      if (this.turnstileSiteKey && !this.turnstileToken) return false
      return true
    },
    formattedAnswer() {
      // Same shared renderer the logged-in consult uses, so the sample
      // answer shows identical linked [n] citations (DOMPurify-sanitized).
      return renderConsultMarkdown(this.answer, this.sources)
    },
    year() {
      return new Date().getFullYear()
    },
    progressButtonLabel() {
      const step = this.progressSteps[this.activeProgressIndex]
      return step?.button || step?.message || 'Working…'
    },
  },
  beforeUnmount() {
    this.stopProgress()
  },
  mounted() {
    document.title = 'Veterinary Media.ai — Veterinary CE Courses & Live Events'
    if (this.turnstileSiteKey) this.loadTurnstile()
  },
  methods: {
    loadTurnstile() {
      if (window.turnstile) {
        this.renderTurnstile()
        return
      }
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.onload = () => this.renderTurnstile()
      document.head.appendChild(script)
    },
    renderTurnstile() {
      if (!window.turnstile || !this.$refs.turnstileContainer) return
      this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileContainer, {
        sitekey: this.turnstileSiteKey,
        callback: (token) => { this.turnstileToken = token },
        'error-callback': () => { this.turnstileToken = '' },
        'expired-callback': () => { this.turnstileToken = '' },
      })
    },
    resetTurnstile() {
      this.turnstileToken = ''
      if (window.turnstile && this.turnstileWidgetId !== null) {
        try { window.turnstile.reset(this.turnstileWidgetId) } catch (e) { /* noop */ }
      }
    },
    startProgress() {
      this.stopProgress()
      this.progressSteps = DEMO_PROGRESS_STEPS.map((s) => ({ ...s, done: false }))
      this.activeProgressIndex = 0
      this.progressTimer = setInterval(() => {
        if (this.activeProgressIndex >= this.progressSteps.length - 1) return
        this.progressSteps[this.activeProgressIndex].done = true
        this.activeProgressIndex += 1
      }, PROGRESS_STEP_MS)
    },
    completeProgress() {
      this.stopProgress()
      this.progressSteps = this.progressSteps.map((s) => ({ ...s, done: true }))
      this.activeProgressIndex = Math.max(this.progressSteps.length - 1, 0)
    },
    stopProgress() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    clearProgress() {
      this.stopProgress()
      this.progressSteps = []
      this.activeProgressIndex = 0
    },
    sourceDbBadgeClass(db) {
      const slug = (db || 'PubMed').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '')
      return `db-badge-${slug}`
    },
    sourceHref(src) {
      if (src?.url) return src.url
      const pmid = src?.pmid || ''
      if (/^\d+$/.test(String(pmid))) return `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`
      return '#'
    },
    sourceIdLabel(src) {
      const db = src?.source_db || 'PubMed'
      const id = src?.pmid || ''
      if (db === 'FDA Animal Drug Label' || String(id).startsWith('fda_')) return 'FDA Label'
      if (db === 'Merck Veterinary Manual') return 'Merck Vet Manual'
      if (db === 'eClinPath (Cornell)') return 'eClinPath'
      if (db === 'AGRICOLA') return `AGRICOLA: ${String(id).replace('ag_', '')}`
      if (String(id).startsWith('ss_')) return `Semantic Scholar: ${String(id).replace('ss_', '')}`
      if (id && /^\d+$/.test(String(id))) return `PMID: ${id}`
      if (id) return `ID: ${id}`
      return db
    },
    async askDemo() {
      if (!this.canSubmit) return
      this.loading = true
      this.error = ''
      this.errorIsLimit = false
      this.answer = ''
      this.sources = []
      this.teasers = { courses: [], videos: [] }
      this.cta = null
      this.showAllSources = false
      this.startProgress()

      try {
        const apiBase = process.env.VUE_APP_API_URL || '/api'
        const resp = await fetch(`${apiBase}/vet-consult/public-demo/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // credentials: 'include' lets the browser round-trip the
          // signed vmg_visitor cookie issued by the demo endpoint so
          // return visits stay attributable (Phase B analytics).
          credentials: 'include',
          body: JSON.stringify({
            query: this.query.trim(),
            turnstile_token: this.turnstileToken,
          }),
        })

        const data = await resp.json().catch(() => ({}))
        if (!resp.ok) {
          this.clearProgress()
          this.error = data.message || 'We could not generate a sample answer. Please try again.'
          this.errorIsLimit = resp.status === 429
          this.cta = data.cta || null
          return
        }

        this.completeProgress()
        this.answer = data.answer || ''
        this.sources = data.sources || []
        this.teasers = data.teasers || { courses: [], videos: [] }
        this.cta = data.cta || null
        // Brief beat so visitors see the checklist finish before the answer.
        await new Promise((r) => setTimeout(r, 350))
      } catch (err) {
        this.clearProgress()
        this.error = 'Network error. Please check your connection and retry.'
      } finally {
        this.loading = false
        this.clearProgress()
        this.resetTurnstile()
      }
    },
    onSignupSuccess() {
      // Account is created and JWT stored by the auth service; the backend
      // has already backfilled this visitor's anonymous demo questions onto
      // the new user. Send them into Vet Consult (not the course catalog).
      // License/SCAV verification is an optional follow-up, not a gate.
      this.$router.push(learningHomeLocation(this.orgSlug))
    },
  },
}
</script>

<style scoped>
.vmg-public {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #1E293B;
  background: #FFFFFF;
  min-height: 100vh;
}

.vmg-container { max-width: 1100px; margin: 0 auto; padding: 0 24px; }

.vmg-header { background: #1B4F8C; color: #fff; position: sticky; top: 0; z-index: 50; }
.vmg-nav { display: flex; align-items: center; justify-content: space-between; padding: 16px 24px; }
.vmg-brand { display: flex; align-items: center; gap: 10px; }
.vmg-brand-mark { background: #D4A017; color: #1B4F8C; font-weight: 800; padding: 4px 8px; border-radius: 6px; letter-spacing: 1px; }
.vmg-brand-text-block { display: flex; flex-direction: column; line-height: 1.2; }
.vmg-brand-text { font-weight: 600; }
.vmg-brand-tagline { font-size: 0.72rem; color: rgba(255,255,255,0.75); font-weight: 400; }
.vmg-nav-links { display: flex; gap: 18px; align-items: center; }
.vmg-link { color: #fff; text-decoration: none; font-size: 14px; opacity: 0.85; }
.vmg-link:hover { opacity: 1; }
.vmg-link-cta { background: #D4A017; color: #1B4F8C; padding: 8px 14px; border-radius: 8px; font-weight: 600; }

.vmg-hero { padding: 80px 0 60px; background: linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%); }
.vmg-hero-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 48px; align-items: center; }
.vmg-eyebrow { color: #D4A017; font-weight: 600; letter-spacing: 1px; text-transform: uppercase; font-size: 12px; margin-bottom: 12px; }
.vmg-members-badge {
  display: inline-block;
  background: rgba(212, 160, 23, 0.12);
  border: 1px solid rgba(212, 160, 23, 0.35);
  padding: 6px 12px;
  border-radius: 999px;
}
.vmg-h1 { font-size: 40px; line-height: 1.15; color: #1B4F8C; margin-bottom: 12px; }
.vmg-hero-sub { font-size: 22px; font-weight: 600; color: #1B4F8C; margin-bottom: 12px; }
.vmg-h2 { font-size: 32px; color: #1B4F8C; margin-bottom: 12px; }
.vmg-lede { font-size: 17px; color: #475569; margin-bottom: 24px; }
.vmg-sub { color: #64748B; margin-bottom: 24px; }
.vmg-bullets { list-style: none; padding: 0; margin: 0 0 28px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
.vmg-bullets li { padding-left: 24px; position: relative; color: #1E293B; }
.vmg-bullets li::before { content: '✓'; position: absolute; left: 0; color: #1B4F8C; font-weight: 700; }

.vmg-cta-row { display: flex; gap: 12px; flex-wrap: wrap; }
.vmg-btn { display: inline-block; padding: 12px 22px; border-radius: 10px; text-decoration: none; font-weight: 600; cursor: pointer; border: 0; font-size: 15px; transition: transform .1s ease, opacity .15s ease; }
.vmg-btn:hover { transform: translateY(-1px); }
.vmg-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
.vmg-btn-primary { background: #1B4F8C; color: #fff; }
.vmg-btn-ghost { background: transparent; color: #1B4F8C; border: 2px solid #1B4F8C; }

.vmg-hero-card { background: #fff; border: 1px solid #E2E8F0; border-radius: 16px; padding: 24px; box-shadow: 0 8px 32px rgba(15,31,61,0.08); }
.vmg-card-eyebrow { font-size: 12px; color: #64748B; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 12px; }
.vmg-card-stack { display: grid; gap: 8px; margin-bottom: 18px; }
.vmg-stack-row { padding: 10px 14px; background: #F1F5F9; border-radius: 8px; font-size: 14px; font-weight: 500; }
.vmg-card-foot { font-size: 13px; color: #64748B; line-height: 1.5; }

.vmg-try { padding: 80px 0; background: #F8FAFC; }
.vmg-try-form { background: #fff; padding: 24px; border-radius: 16px; border: 1px solid #E2E8F0; box-shadow: 0 4px 16px rgba(15,31,61,0.06); }
.vmg-textarea { width: 100%; padding: 14px; border: 1px solid #CBD5E1; border-radius: 10px; font-family: inherit; font-size: 16px; resize: vertical; min-height: 84px; }
.vmg-textarea:focus { outline: 2px solid #1B4F8C; outline-offset: 1px; border-color: transparent; }
.vmg-form-actions { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; }
.vmg-counter { color: #94A3B8; font-size: 12px; }
.vmg-turnstile { margin-top: 16px; min-height: 65px; }

/* Staged agent progress — open-site reassurance while demo POST runs */
.vmg-progress {
  margin-top: 18px;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  box-shadow: 0 4px 16px rgba(15,31,61,0.06);
}
.vmg-progress-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1B4F8C;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 12px;
}
.vmg-progress-step {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 0;
  font-size: 14px;
  color: #94A3B8;
  transition: color 0.2s ease;
}
.vmg-progress-step.is-active { color: #1E293B; font-weight: 600; }
.vmg-progress-step.is-done { color: #475569; }
.vmg-progress-icon {
  width: 18px;
  flex-shrink: 0;
  text-align: center;
  margin-top: 1px;
}
.vmg-progress-step.is-done .vmg-progress-icon { color: #059669; }
.vmg-progress-step.is-active .vmg-progress-icon { color: #1B4F8C; }
.vmg-progress-text { line-height: 1.4; }

.vmg-error { background: #FEF2F2; color: #B91C1C; padding: 14px 18px; border-radius: 10px; margin-top: 18px; font-size: 14px; display: flex; gap: 12px; align-items: center; flex-wrap: wrap; }
.vmg-error-cta { color: #1B4F8C; font-weight: 600; text-decoration: none; }
.vmg-error-cta:hover { text-decoration: underline; }

.vmg-answer { margin-top: 28px; background: #fff; border: 1px solid #E2E8F0; border-radius: 16px; padding: 28px; }
.vmg-answer-title { color: #1B4F8C; margin: 0 0 12px; font-size: 20px; }
.vmg-answer-body {
  line-height: 1.65;
  color: #1E293B;
  font-size: 0.9375rem;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
}
/* Match logged-in consult typography so sample answers feel identical */
.vmg-answer-body :deep(p) { margin: 0 0 7px; }
.vmg-answer-body :deep(p:last-child) { margin-bottom: 0; }
.vmg-answer-body :deep(h3) { font-size: 14px; font-weight: 700; margin: 10px 0 5px; color: #0f172a; }
.vmg-answer-body :deep(h4) { font-size: 13px; font-weight: 600; margin: 8px 0 4px; color: #1e293b; }
.vmg-answer-body :deep(ul) { margin: 5px 0; padding-left: 18px; }
.vmg-answer-body :deep(li) { margin-bottom: 3px; }
.vmg-answer-body :deep(code) {
  background: #f1f5f9;
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.85em;
  font-family: monospace;
}
.vmg-answer-body :deep(.citation-link) { text-decoration: none; }
.vmg-answer-body :deep(sup.citation) { color: #2563eb; font-weight: 700; font-size: 10px; }
.vmg-answer-body :deep(a.citation-link:hover sup.citation) { text-decoration: underline; color: #1d4ed8; }

/* Research sources — card layout aligned with LmsAgentChat */
.vmg-sources { margin-top: 24px; padding-top: 20px; border-top: 1px solid #E2E8F0; }
.vmg-sources-title { display: flex; align-items: center; gap: 8px; color: #1B4F8C; font-size: 15px; margin: 0 0 12px; }
.vmg-sources-list { display: flex; flex-direction: column; gap: 8px; }
.vmg-source-item {
  padding: 10px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
}
.vmg-source-meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.vmg-source-db-badge {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.db-badge-pubmed { background: #dbeafe; color: #1d4ed8; }
.db-badge-javma { background: #fef3c7; color: #b45309; }
.db-badge-semantic-scholar { background: #ede9fe; color: #6d28d9; }
.db-badge-agricola { background: #d1fae5; color: #065f46; }
.db-badge-merck-veterinary-manual { background: #fae8ff; color: #86198f; }
.db-badge-eclinpath-cornell { background: #ffe4e6; color: #9f1239; }
.db-badge-fda-animal-drug-label { background: #ffedd5; color: #c2410c; }
.vmg-source-journal { font-size: 0.75rem; font-weight: 600; color: #92400e; }
.vmg-source-year { font-size: 0.6875rem; color: #9ca3af; }
.vmg-source-title-link {
  display: flex;
  align-items: baseline;
  gap: 8px;
  text-decoration: none;
  margin-bottom: 2px;
}
.vmg-source-title-link:hover .vmg-source-name { color: #1B4F8C; text-decoration: underline; }
.vmg-source-num { color: #2563eb; font-weight: 700; font-size: 12px; flex-shrink: 0; }
.vmg-source-name { color: #0f172a; font-size: 13.5px; font-weight: 600; line-height: 1.35; }
.vmg-source-rationale {
  margin: 4px 0 0;
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
  line-height: 1.4;
  display: flex;
  gap: 5px;
  align-items: flex-start;
}
.vmg-source-rationale i { color: #d97706; margin-top: 2px; flex-shrink: 0; }
.vmg-source-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  flex-wrap: wrap;
}
.vmg-source-id {
  font-size: 0.6875rem;
  color: #9ca3af;
  font-family: monospace;
}
.vmg-source-pdf {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #dc2626;
  text-decoration: none;
  padding: 1px 5px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
}
.vmg-sources-more {
  align-self: flex-start;
  margin-top: 2px;
  background: none;
  border: none;
  color: #1B4F8C;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 0;
}
.vmg-sources-more:hover { text-decoration: underline; }
.vmg-cta-card { margin-top: 24px; padding: 24px; background: linear-gradient(135deg, #1B4F8C 0%, #2563B0 100%); color: #fff; border-radius: 14px; }
.vmg-cta-card h4 { margin: 0 0 4px; font-size: 18px; }
.vmg-cta-card p { margin: 0 0 14px; font-size: 14px; opacity: 0.92; }
.vmg-cta-card .vmg-btn-primary { background: #D4A017; color: #1B4F8C; }
.vmg-cta-card .vmg-btn-ghost { color: #fff; border-color: #fff; }
.vmg-cta-foot { margin: 14px 0 0; font-size: 13px; opacity: 0.92; }
.vmg-cta-link { color: #fff; text-decoration: underline; }

/* One-click signup card at the rate-limit wall */
.vmg-signup-card { margin-top: 18px; padding: 24px; background: #fff; border: 1px solid #E2E8F0; border-radius: 14px; box-shadow: 0 4px 16px rgba(15,31,61,0.06); }
.vmg-signup-title { margin: 0 0 4px; color: #1B4F8C; font-size: 19px; }
.vmg-signup-sub { margin: 0 0 16px; color: #475569; font-size: 14px; }
.vmg-signup-foot { margin: 14px 0 0; color: #64748B; font-size: 12.5px; }
.vmg-inline-link { color: #1B4F8C; font-weight: 600; text-decoration: none; }
.vmg-inline-link:hover { text-decoration: underline; }

/* Locked CE / video teasers */
.vmg-teasers { margin-top: 24px; padding-top: 20px; border-top: 1px solid #E2E8F0; }
.vmg-teasers-title { display: flex; align-items: center; gap: 8px; color: #1B4F8C; font-size: 15px; margin: 0 0 14px; }
.vmg-teaser-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 14px; }
.vmg-teaser-card { border: 1px solid #E2E8F0; border-radius: 12px; overflow: hidden; background: #fff; }
.vmg-teaser-thumb { position: relative; height: 96px; background: #E2E8F0 center/cover no-repeat; display: flex; align-items: center; justify-content: center; }
.vmg-teaser-thumb-video { background: linear-gradient(135deg, #1B4F8C 0%, #2563B0 100%); }
.vmg-teaser-lock { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(15,31,61,0.45); color: #fff; font-size: 20px; }
.vmg-teaser-badge { position: absolute; bottom: 6px; right: 6px; background: #D4A017; color: #1B4F8C; font-size: 11px; font-weight: 700; padding: 2px 6px; border-radius: 4px; z-index: 1; }
.vmg-teaser-body { padding: 10px 12px 14px; }
.vmg-teaser-kind { display: inline-block; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; color: #64748B; margin-bottom: 4px; }
.vmg-teaser-name { margin: 0 0 4px; font-size: 13.5px; font-weight: 600; color: #1E293B; line-height: 1.35; }
.vmg-teaser-meta { margin: 0; font-size: 12px; color: #64748B; }

.vmg-scav { padding: 80px 0; }
.vmg-scav-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 56px; align-items: center; }
.vmg-benefits { list-style: none; padding: 24px; background: #F8FAFC; border-radius: 16px; border: 1px solid #E2E8F0; margin: 0; }
.vmg-benefits li { padding: 12px 0 12px 28px; position: relative; border-bottom: 1px solid #E2E8F0; }
.vmg-benefits li:last-child { border-bottom: 0; }
.vmg-benefits li::before { content: '★'; position: absolute; left: 0; color: #D4A017; }

.vmg-footer { background: #0F1F3D; color: #CBD5E1; padding: 32px 0; }
.vmg-footer p { margin: 4px 0; font-size: 13px; }
.vmg-disclaimer { color: #94A3B8; }

@media (max-width: 880px) {
  .vmg-hero-grid, .vmg-scav-grid { grid-template-columns: 1fr; gap: 32px; }
  .vmg-h1 { font-size: 34px; }
  .vmg-bullets { grid-template-columns: 1fr; }
}
</style>
